/**
 * Journey rail — the v1 "YOUR JOURNEY" checklist, rebuilt on v2 chapters.
 *
 * v1's most reassuring screen element: the learner can see the whole shape
 * of the walk before they start, and where they are inside it. It shows
 * chapters and rough minutes — never "question N of 72", which is the one
 * number that makes a discovery experience feel like an exam.
 */
import { Check } from "lucide-react";
import { chaptersFor } from "../chapters";
import type { Band } from "../band";
import { cn } from "@/lib/utils";

/** Rough minutes per chapter, derived from screen count at ~25s a decision. */
function minutesFor(screenCount: number): number {
  return Math.max(1, Math.round((screenCount * 25) / 60));
}

interface Props {
  band: Band;
  /** Chapter id the learner is currently inside, if the walk has started. */
  currentChapterId?: string | null;
  className?: string;
}

export function JourneyRail({ band, currentChapterId, className }: Props) {
  const chapters = chaptersFor(band);
  const currentIndex = currentChapterId
    ? chapters.findIndex((c) => c.id === currentChapterId)
    : -1;

  return (
    <nav aria-label="Your journey" className={cn("rounded-3xl border border-border bg-card p-6", className)}>
      <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your journey</h3>

      <ol className="mt-5 space-y-0">
        <Step
          label="Start"
          state={currentIndex >= 0 ? "done" : "current"}
          isLast={false}
        />
        {chapters.map((c, i) => (
          <Step
            key={c.id}
            label={c.title}
            meta={`~ ${minutesFor(c.screens.length)} min`}
            state={
              currentIndex < 0
                ? "upcoming"
                : i < currentIndex
                  ? "done"
                  : i === currentIndex
                    ? "current"
                    : "upcoming"
            }
            isLast={false}
          />
        ))}
        <Step
          label="Your results"
          meta="~ 2 min"
          state="upcoming"
          isLast
          detail={
            band === "12to14"
              ? ["Your Career Worlds", "Your archetype", "What to try next"]
              : ["Your career matches", "Your Career DNA™", "Why each one matched"]
          }
        />
      </ol>
    </nav>
  );
}

function Step({
  label, meta, state, isLast, detail,
}: {
  label: string;
  meta?: string;
  state: "done" | "current" | "upcoming";
  isLast: boolean;
  detail?: string[];
}) {
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!isLast && (
        <span
          aria-hidden="true"
          className="absolute left-[11px] top-6 bottom-0 w-px bg-border"
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          "relative z-10 mt-0.5 grid h-[23px] w-[23px] shrink-0 place-items-center rounded-full border-2",
          state === "done" && "border-primary bg-primary text-primary-foreground",
          state === "current" && "border-foreground bg-background",
          state === "upcoming" && "border-border bg-background",
        )}
      >
        {state === "done" && <Check className="h-3 w-3" />}
      </span>
      <div className="min-w-0">
        <p
          className={cn(
            "text-sm leading-snug",
            state === "current" && "font-semibold text-foreground",
            state === "done" && "text-foreground/80",
            state === "upcoming" && "text-muted-foreground",
          )}
        >
          {label}
          {state === "current" && <span className="sr-only"> (you are here)</span>}
        </p>
        {meta && <p className="mt-0.5 text-xs text-primary/80">{meta}</p>}
        {detail && (
          <ul className="mt-2 space-y-1">
            {detail.map((d) => (
              <li key={d} className="text-sm text-foreground/70">{d}</li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
